import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import PurchaseDocumentForm from '../components/PurchaseDocumentForm'
import { formatDate } from '../utils/dateFormat'
import { apiGet, apiPost, apiPut } from '../utils/api'

// Функция для форматирования чисел с разделителями тысяч
const formatNumber = (num: number): string => {
  return Math.round(num).toLocaleString('ru-RU')
}

interface Row { id: string; name: string; unit: string; opening: number; incoming: number; outgoing: number; closing: number }

const ReportsInventoryMovements: React.FC = () => {
  const navigate = useNavigate()
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [isInitialized, setIsInitialized] = useState(false)
  const [lastValidFrom, setLastValidFrom] = useState('')
  const [lastValidTo, setLastValidTo] = useState('')
  
  // Загружаем сохраненный период и режим при инициализации
  useEffect(() => {
    const savedFrom = localStorage.getItem('inventory-movements-report-from')
    const savedTo = localStorage.getItem('inventory-movements-report-to')
    const savedMode = localStorage.getItem('inventory-movements-report-mode')
    
    if (savedFrom) setFrom(savedFrom)
    if (savedTo) setTo(savedTo)
    if (savedMode && ['flat', 'item-warehouse', 'warehouse-item'].includes(savedMode)) {
      setMode(savedMode as 'flat'|'item-warehouse'|'warehouse-item')
    }
    setIsInitialized(true)
  }, [])
  const [mode, setMode] = useState<'flat'|'item-warehouse'|'warehouse-item'>('flat')
  const [grouped, setGrouped] = useState<any>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [detailsTitle, setDetailsTitle] = useState('')
  const [details, setDetails] = useState<Array<{date:string;document:string;type:string;quantity:number;unitPrice:number|null;totalPrice:number|null;warehouse?:string}>>([])

  // Убрана логика автоматического открытия модального окна по URL параметрам

  // Для открытия документа закупки прямо из расшифровки
  const [docFormOpen, setDocFormOpen] = useState(false)
  const [docInitial, setDocInitial] = useState<any>(null)
  const [suppliers, setSuppliers] = useState<any[]>([])
  const [inventoryItems, setInventoryItems] = useState<any[]>([])
  const [warehouses, setWarehouses] = useState<any[]>([])

  // Сохраняем период в localStorage
  const savePeriod = (fromDate: string, toDate: string) => {
    if (fromDate) {
      localStorage.setItem('inventory-movements-report-from', fromDate)
    } else {
      localStorage.removeItem('inventory-movements-report-from')
    }
    if (toDate) {
      localStorage.setItem('inventory-movements-report-to', toDate)
    } else {
      localStorage.removeItem('inventory-movements-report-to')
    }
  }

  // Сохраняем режим в localStorage
  const saveMode = (newMode: 'flat'|'item-warehouse'|'warehouse-item') => {
    localStorage.setItem('inventory-movements-report-mode', newMode)
  }

  // Обработчик изменения режима
  const handleModeChange = (newMode: 'flat'|'item-warehouse'|'warehouse-item') => {
    setMode(newMode)
    saveMode(newMode)
  }

  // Сброс периода и режима
  const resetPeriod = () => {
    setFrom('')
    setTo('')
    setMode('flat')
    setLastValidFrom('')
    setLastValidTo('')
    localStorage.removeItem('inventory-movements-report-from')
    localStorage.removeItem('inventory-movements-report-to')
    localStorage.removeItem('inventory-movements-report-mode')
  }

  const fetchData = async () => {
    setLoading(true)
    setError('')
    try {
      const params: Record<string, string> = {}
      if (from) params.from = from
      if (to) params.to = to
      if (mode !== 'flat') params.group = mode
      const data = await apiGet<any>('/reports/inventory-movements', params)
      
      if (mode === 'flat') {
        setRows(Array.isArray(data.items) ? data.items : [])
        setGrouped(null)
      } else {
        // Для режимов группировки сохраняем всю структуру данных
        setGrouped(data || { items: [], warehouses: [] })
        setRows([])
      }
    } catch (e: any) {
      console.error('Error fetching inventory movements:', e)
      setError(e.message || 'Ошибка загрузки отчета')
      setGrouped(null)
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  // Отслеживаем последний режим для корректного определения изменений
  const [lastMode, setLastMode] = useState<'flat'|'item-warehouse'|'warehouse-item' | null>(null)

  // Загружаем данные только при реальном изменении дат или режима
  useEffect(() => {
    if (!isInitialized) return

    const modeChanged = lastMode !== null && mode !== lastMode
    const fromChanged = from !== lastValidFrom
    const toChanged = to !== lastValidTo
    
    // Загружаем при первой инициализации или при изменении режима/дат
    if (lastMode === null || modeChanged || fromChanged || toChanged) {
      const timer = setTimeout(() => {
        fetchData()
        setLastValidFrom(from)
        setLastValidTo(to)
        setLastMode(mode)
      }, 300)
      
      return () => clearTimeout(timer)
    }
  }, [isInitialized, from, to, mode, lastValidFrom, lastValidTo, lastMode])

  const openDocByNumber = async (docNumber: string) => {
    try {
      const data = await apiGet<{ purchases?: any[]; items?: any[] }>('/purchases', { search: docNumber, limit: 1 })
      const doc = Array.isArray(data.items) ? null : (Array.isArray(data.purchases) ? data.purchases.find((d:any)=>d.number===docNumber) : null)
      if (!doc) return
      const [supJson, invJson, whJson] = await Promise.all([
        apiGet<{ suppliers?: any[] }>('/suppliers?limit=1000'),
        apiGet<{ items?: any[] }>('/inventory-items?limit=1000'),
        apiGet<any[]>('/warehouses')
      ])
      setSuppliers(Array.isArray(supJson) ? [] : (supJson.suppliers || []))
      setInventoryItems(Array.isArray(invJson) ? invJson : (invJson.items || []))
      setWarehouses(Array.isArray(whJson) ? whJson : [])
      setDocInitial(doc)
      setDocFormOpen(true)
    } catch (e) {}
  }

  const total = rows.reduce((acc, r) => ({
    opening: acc.opening + (r?.opening || 0),
    incoming: acc.incoming + (r?.incoming || 0),
    outgoing: acc.outgoing + (r?.outgoing || 0),
    closing: acc.closing + (r?.closing || 0),
  }), { opening: 0, incoming: 0, outgoing: 0, closing: 0 })

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Движение ТМЦ</h2>
        <div className="flex items-center gap-2">
          <select value={mode} onChange={e=> handleModeChange(e.target.value as any)} className="border rounded px-2 py-1 text-sm">
            <option value="flat">Плоский список</option>
            <option value="item-warehouse">Номенклатура → Склады</option>
            <option value="warehouse-item">Склады → Номенклатура</option>
          </select>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="border rounded px-2 py-1 text-sm" placeholder="Дата с" />
          <input type="date" value={to} onChange={e => setTo(e.target.value)} className="border rounded px-2 py-1 text-sm" placeholder="Дата по" />
          <button onClick={() => { savePeriod(from, to); fetchData(); }} className="px-3 py-2 text-sm bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 border">Обновить</button>
          <button onClick={resetPeriod} className="px-3 py-2 text-sm bg-red-100 text-red-800 rounded-md hover:bg-red-200 border">Сбросить</button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200">{error}</div>}
      {loading ? <p className="text-gray-600">Загрузка...</p> : mode !== 'flat' ? (
        mode === 'item-warehouse' && grouped?.items && Array.isArray(grouped.items) ? (
          <div className="bg-white rounded-lg shadow border">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr className="text-xs text-gray-500">
                  <th className="text-left py-2 px-4 font-medium">ТМЦ</th>
                  <th className="text-right py-2 px-2 font-medium">Нач.ост.</th>
                  <th className="text-right py-2 px-2 font-medium">Приход</th>
                  <th className="text-right py-2 px-2 font-medium">Расход</th>
                  <th className="text-right py-2 px-2 font-medium">Кон.ост.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(() => {
                  // Фильтруем: показываем только элементы с ненулевыми значениями
                  const validItems = grouped.items.filter((it: any) => {
                    if (!it || !it.id || !it.name) return false
                    // Проверяем total значения - показываем если хотя бы одно не равно нулю
                    const total = it.total || {}
                    return (total.opening !== 0 || total.incoming !== 0 || total.outgoing !== 0 || total.closing !== 0)
                  })
                  return validItems.map((it: any) => (
                  <React.Fragment key={it.id}>
                    {/* Итоговая строка для ТМЦ */}
                    <tr className="bg-gray-50 font-medium hover:bg-gray-100 cursor-pointer" onClick={async () => {
                      setDetailsOpen(true)
                      setDetailsTitle(it.name)
                      try {
                        const params: Record<string, string> = {}
                        if (from) params.from = from
                        if (to) params.to = to
                        const data = await apiGet<any>(`/reports/inventory-movements/${it.id}/details`, params)
                        const raw = Array.isArray(data.items) ? data.items : []
                        const enhanced = raw.map((row: any) => row)
                        setDetails(enhanced)
                      } catch (e) {
                        console.error('Error fetching details:', e)
                        setDetails([])
                      }
                    }}>
                      <td className="py-2 px-4 text-sm text-left">
                        {it.name} <span className="text-gray-500">({it.unit})</span>
                      </td>
                      <td className="py-2 px-2 text-sm text-right">{formatNumber(it.total?.opening ?? it.opening ?? 0)}</td>
                      <td className="py-2 px-2 text-sm text-right">{formatNumber(it.total?.incoming ?? it.incoming ?? 0)}</td>
                      <td className="py-2 px-2 text-sm text-right">{formatNumber(it.total?.outgoing ?? it.outgoing ?? 0)}</td>
                      <td className="py-2 px-2 text-sm text-right">{formatNumber(it.total?.closing ?? it.closing ?? 0)}</td>
                    </tr>
                    {/* Детализация по складам */}
                    {(() => {
                      const warehouses = it.warehouses || []
                      // Показываем только склады с ненулевыми значениями
                      const validWarehouses = warehouses.filter((w: any) => 
                        w && w.id && w.name && (w.opening !== 0 || w.incoming !== 0 || w.outgoing !== 0 || w.closing !== 0)
                      )
                      return validWarehouses.map((w: any) => (
                      <tr key={`${it.id}-${w.id}`} className="hover:bg-gray-50 cursor-pointer" onClick={async () => {
                        setDetailsOpen(true)
                        setDetailsTitle(`${it.name} - ${w.name}`)
                        try {
                          const params: Record<string, string> = {
                            warehouseId: w.id
                          }
                          if (from) params.from = from
                          if (to) params.to = to
                          const data = await apiGet<any>(`/reports/inventory-movements/${it.id}/details`, params)
                          const raw = Array.isArray(data.items) ? data.items : []
                          const enhanced = raw.map((row: any) => row)
                          setDetails(enhanced)
                        } catch (e) {
                          console.error('Error fetching warehouse details:', e)
                          setDetails([])
                        }
                      }}>
                        <td className="py-1 px-4 text-xs pl-8 text-left">{w.name || '-'}</td>
                        <td className="py-1 px-2 text-xs text-right">{formatNumber(w.opening || 0)}</td>
                        <td className="py-1 px-2 text-xs text-right">{formatNumber(w.incoming || 0)}</td>
                        <td className="py-1 px-2 text-xs text-right">{formatNumber(w.outgoing || 0)}</td>
                        <td className="py-1 px-2 text-xs text-right">{formatNumber(w.closing || 0)}</td>
                      </tr>
                      ))
                    })()}
                  </React.Fragment>
                  ))
                })()}
              </tbody>
            </table>
          </div>
        ) : mode === 'item-warehouse' ? (
          <p className="text-gray-600">Нет данных.</p>
        ) : mode === 'warehouse-item' && grouped?.warehouses && Array.isArray(grouped.warehouses) ? (
          <div className="space-y-2">
            {(grouped.warehouses || []).filter((w:any) => 
              w && w.items && Array.isArray(w.items) && w.items.some((it:any) => 
                it && (it.opening !== 0 || it.incoming !== 0 || it.outgoing !== 0 || it.closing !== 0)
              )
            ).map((w:any)=> (
              <details key={w.warehouseId} className="bg-white rounded border">
                <summary className="cursor-pointer select-none px-4 py-2 font-medium">{w.warehouseName}</summary>
                <div className="px-4 pb-3">
                  <table className="min-w-full">
                    <thead>
                      <tr className="text-xs text-gray-500">
                        <th className="text-left py-1">ТМЦ</th>
                        <th className="text-right py-1">Нач.ост.</th>
                        <th className="text-right py-1">Приход</th>
                        <th className="text-right py-1">Расход</th>
                        <th className="text-right py-1">Кон.ост.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(w.items || []).filter((it:any) => 
                        it && (it.opening !== 0 || it.incoming !== 0 || it.outgoing !== 0 || it.closing !== 0)
                      ).map((it:any)=> (
                        <tr key={it.id} className="text-sm">
                          <td className="py-1">{it.name || '-'}</td>
                          <td className="py-1 text-right">{formatNumber(it.opening || 0)}</td>
                          <td className="py-1 text-right">{formatNumber(it.incoming || 0)}</td>
                          <td className="py-1 text-right">{formatNumber(it.outgoing || 0)}</td>
                          <td className="py-1 text-right">{formatNumber(it.closing || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        ) : <p className="text-gray-600">Нет данных.</p>
      ) : (
        <div className="overflow-x-auto bg-white shadow rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ТМЦ</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ед.</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Нач.ост.</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Приход</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Расход</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Кон.ост.</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 cursor-pointer" onClick={async () => {
                  setDetailsOpen(true)
                  setDetailsTitle(r.name)
                  try {
                    const params: Record<string, string> = {}
                    if (from) params.from = from
                    if (to) params.to = to
                    const data = await apiGet<any>(`/reports/inventory-movements/${r.id}/details`, params)
                    const raw = Array.isArray(data.items) ? data.items : []
                    // Подменяем «Расход ТМЦ #<id>» на «Расход ТМЦ #<number>», дополнительно сохраняя id для навигации
                    // Сервер теперь может прислать consumptionId и уже готовый «Расход ТМЦ #INV-OUT-…»
                    const enhanced = raw.map((row: any) => row)
                    setDetails(enhanced)
                    // URL остается неизменным при открытии модального окна
                  } catch (e) {
                    setDetails([])
                  }
                }}>
                  <td className="px-4 py-2 text-sm text-gray-900 text-left">{r?.name || '-'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600 text-left">{r?.unit || '-'}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(r?.opening || 0)}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(r?.incoming || 0)}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(r?.outgoing || 0)}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatNumber(r?.closing || 0)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 text-base font-bold">
                <td className="px-4 py-2 text-left font-bold">Итого</td>
                <td className="px-4 py-2 text-left" />
                <td className="px-4 py-2 text-right">{formatNumber(total.opening)}</td>
                <td className="px-4 py-2 text-right">{formatNumber(total.incoming)}</td>
                <td className="px-4 py-2 text-right">{formatNumber(total.outgoing)}</td>
                <td className="px-4 py-2 text-right">{formatNumber(total.closing)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      {detailsOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black opacity-30" onClick={() => setDetailsOpen(false)} />
          <div className="relative bg-white rounded-lg shadow-lg border w-full max-w-3xl mx-4">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">{detailsTitle}: расшифровка</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setDetailsOpen(false)}>✕</button>
            </div>
            <div className="p-4 overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Дата</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Документ</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Тип</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Кол-во</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Цена</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Сумма</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Склад</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {details.map((d, idx) => {
                    const formattedDoc = (() => {
                      const text = d.document || ''
                      // Номер закупки — кликабельный
                      if (/^INV-PUR-\d{4}-\d{4}$/.test(text)) {
                        return (
                          <button
                            className="text-blue-700 hover:text-blue-900 underline whitespace-nowrap"
                            onClick={() => openDocByNumber(text)}
                          >
                            {text}
                          </button>
                        )
                      }
                      // «Расход ТМЦ #INV-RAS-…», «Расход ТМЦ #INV-OUT-…» или «Расход ТМЦ #CONSUMP-…» — ссылка на документ; сервер мог прислать consumptionId
                      if (/^Расход ТМЦ\s+#(INV-RAS|INV-OUT|CONSUMP)-\d{4}-\d{4}$/i.test(text)) {
                        const id = (d as any).consumptionId
                        const back = encodeURIComponent(window.location.pathname + window.location.search)
                        return (
                          <button
                            className="text-blue-700 hover:text-blue-900 underline whitespace-nowrap"
                            onClick={() => id ? navigate(`/inventory-consumptions?open=${id}&return=${back}`) : undefined}
                          >
                            {text}
                          </button>
                        )
                      }
                      // «Расход ТМЦ» без номера — ссылка на документ, если есть consumptionId
                      if (text === 'Расход ТМЦ' && (d as any).consumptionId) {
                        const id = (d as any).consumptionId
                        const back = encodeURIComponent(window.location.pathname + window.location.search)
                        return (
                          <button
                            className="text-blue-700 hover:text-blue-900 underline whitespace-nowrap"
                            onClick={() => navigate(`/inventory-consumptions?open=${id}&return=${back}`)}
                          >
                            {text}
                          </button>
                        )
                      }
                      // «Продажа ТМЦ #INV-SAL-…» — ссылка на документ продажи
                      if (/^Продажа ТМЦ\s+#INV-SAL-\d{4}-\d{4}$/i.test(text)) {
                        const id = (d as any).consumptionId // Используем consumptionId для совместимости
                        const back = encodeURIComponent(window.location.pathname + window.location.search)
                        return (
                          <button
                            className="text-blue-700 hover:text-blue-900 underline whitespace-nowrap"
                            onClick={() => id ? navigate(`/inventory-sales?open=${id}&return=${back}`) : undefined}
                          >
                            {text}
                          </button>
                        )
                      }
                      // «Продажа ТМЦ» без номера — ссылка на документ продажи, если есть consumptionId
                      if (text === 'Продажа ТМЦ' && (d as any).consumptionId) {
                        const id = (d as any).consumptionId
                        const back = encodeURIComponent(window.location.pathname + window.location.search)
                        return (
                          <button
                            className="text-blue-700 hover:text-blue-900 underline whitespace-nowrap"
                            onClick={() => navigate(`/inventory-sales?open=${id}&return=${back}`)}
                          >
                            {text}
                          </button>
                        )
                      }
                      // «Возврат ТМЦ INV-RET-…» — ссылка на документ; сервер мог прислать returnId
                      if (/^Возврат ТМЦ\s+INV-RET-\d{4}-\d{4}$/i.test(text)) {
                        const id = (d as any).returnId
                        const back = encodeURIComponent(window.location.pathname + window.location.search)
                        return (
                          <button
                            className="text-blue-700 hover:text-blue-900 underline whitespace-nowrap"
                            onClick={() => id ? navigate(`/inventory-returns?open=${id}&return=${back}`) : undefined}
                          >
                            {text}
                          </button>
                        )
                      }
                      // Старый формат «Расход ТМЦ #<id>» — на всякий случай оставим поддержку (клик откроет по id)
                      const m = text.match(/^Расход ТМЦ\s+#([a-z0-9]+)$/i)
                      if (m) {
                        const id = m[1]
                        return (
                          <button
                            className="text-blue-700 hover:text-blue-900 underline whitespace-nowrap"
                            onClick={() => navigate(`/inventory-consumptions?open=${id}`)}
                          >
                            {text}
                          </button>
                        )
                      }
                      return <span className="whitespace-nowrap">{text || '-'}</span>
                    })()

                    const qty = Number(d.quantity || 0) * (d.type === 'INCOMING' ? 1 : -1)
                    return (
                      <tr key={idx}>
                        <td className="px-3 py-2 text-sm text-gray-700">{formatDate(d.date)}</td>
                        <td className="px-3 py-2 text-sm">{formattedDoc}</td>
                        <td className="px-3 py-2 text-sm text-gray-700">{d.type}</td>
                        <td className="px-3 py-2 text-sm text-right text-gray-900">{qty.toFixed(2)}</td>
                        <td className="px-3 py-2 text-sm text-right text-gray-900">{d.unitPrice != null ? Number(d.unitPrice).toFixed(2) : '-'}</td>
                        <td className="px-3 py-2 text-sm text-right text-gray-900">{d.totalPrice != null ? Number(d.totalPrice).toFixed(2) : '-'}</td>
                        <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{d.warehouse || '-'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div className="px-4 py-3 border-t flex items-center justify-end">
              <button className="px-3 py-2 text-sm rounded-md border text-gray-700 bg-white hover:bg-gray-50" onClick={() => setDetailsOpen(false)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}

      {docFormOpen && (
        <PurchaseDocumentForm
          title={`Редактировать документ ${docInitial?.number || ''}`}
          suppliers={suppliers}
          inventoryItems={inventoryItems}
          warehouses={warehouses}
          initial={docInitial}
          onCancel={() => { setDocFormOpen(false); setDocInitial(null) }}
          onSubmit={async (payload) => {
            try {
              await apiPut(`/purchases/${docInitial.id}`, {
                ...payload,
                defaultWarehouseId: payload.defaultWarehouseId
              })
              setDocFormOpen(false)
              setDocInitial(null)
              fetchData()
            } catch (e) {}
          }}
        />
      )}
    </div>
  )
}

export default ReportsInventoryMovements


